import { toInputDate } from "@/utils/dateUtils";
import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DollarSign,
  BarChart3,
  ExternalLink,
  Edit,
  Trash2,
  Clock,
} from "lucide-react";
import { LicensePlate } from "@/components/my-auctions/LicensePlate";
import { VehicleInformation } from "@/components/car-listing/VehicleInformation";
import { ListingLeadbox } from "@/types/listing";
import {
  getTypeColor,
  getTypeLabel,
  getVehicleStatusColor,
  getVehicleStatusLabel,
  resolveSellerDisplayName,
  resolveSellerSubtitle,
} from "@/utils/listingUtils";
import {
  MyAuctionsStatsDialog,
  type AuctionStats,
} from "@/components/my-auctions/MyAuctionsStatsDialog";
import { useNavigate } from "react-router-dom";
import { CreateListingDialog } from "../CreateListingDialog";
import { EditTestDialog } from "../../test/EditTestDialog";
import { useListingManagement } from "@/hooks/useListingManagement";
import { useTest } from "@/hooks/useTest";
import { useCampaign } from "@/hooks/useCampaigns";
import { useListing } from "@/hooks/useListing";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";
import { useLanguage } from "@/context/LanguageContext";
import { adminLabels } from "@/i18n/labels/adminLabels";
import { useCurrentAdminPermissions } from "@/hooks/useCurrentAdminPermissions";
import { formatDateTime } from "@/utils/dateUtils";

interface ListingDetailsOverviewProps {
  listing: ListingLeadbox | null;
}

export const ListingDetailsOverview: React.FC<ListingDetailsOverviewProps> = ({
  listing,
}) => {
  const { language } = useLanguage();
  const L =
    (adminLabels(language) as { leadboxListings?: Record<string, string> })
      ?.leadboxListings ?? {};
  const [showStats, setShowStats] = useState(false);
  const [showEditListing, setShowEditListing] = useState(false);
  const [showEditTestDialog, setShowEditTestDialog] = useState(false);
  const [showCancelConfirmDialog, setShowCancelConfirmDialog] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [statsData, setStatsData] = useState<AuctionStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.type === UserType.Admin;
  const { canViewDashboardOverview } = useCurrentAdminPermissions();
  const revealBidderIdentities = isAdmin && canViewDashboardOverview;

  const navigate = useNavigate();

  // Get listing details query for refetching
  const { cancelListingAsync, updateListingAsync, isUpdatingListing } =
    useListingManagement();

  // Get listing bids hook
  const { getListingBids } = useListing();
  const listingBidsQuery = getListingBids(listing?.id || "", {
    enabled: false,
  });

  // Get vehicle test data (load immediately for VehicleInformation)
  const { useGetTestById } = useTest();
  const vehicleTestQuery = useGetTestById(listing?.vehicleTestId);
  const vehicleTest = vehicleTestQuery.data;

  // Get campaign data to display campaign name
  const { campaign } = useCampaign(listing?.campaignId || "");

  if (!listing) {
    return <div>Ingen annons vald</div>;
  }

  // Show loading until both listing and vehicleTest are loaded
  if (vehicleTestQuery.isLoading) {
    return <div>Laddar...</div>;
  }

  const isBuyNowTriggered = listing.auctionDetails?.isBuyNowTriggered === true;
  const auctionDisplayPrice =
    listing.listingType === "auction"
      ? ((listing.status === "completed" ||
        listing.status === "reserved" ||
        listing.status === "ended"
          ? isBuyNowTriggered
            ? (listing.auctionDetails?.currentBuyNowPrice ??
              listing.auctionDetails?.buyNowFixed)
            : listing.auctionDetails?.currentBid
          : listing.auctionDetails?.currentBid) ??
        listing.auctionDetails?.startingPrice ??
        0)
      : 0;

  // Handlers
  const handleGoToListing = () => {
    if (listing && listing.campaignId) {
      navigate(`/auctions/${listing.campaignId}/${listing.id}`, {
        state: { from: "/admin/leadbox/listings" },
      });
    }
  };

  const handleStartEditing = () => {
    setShowEditListing(true);
  };

  // Check if listing can be cancelled
  const canCancelListing = () => {
    if (!listing || listing.status !== "active") return false;

    if (listing.listingType === "direct-purchase") {
      return true; // Direct purchase with active status can be cancelled
    }

    if (listing.listingType === "auction") {
      // Auction can be cancelled only if no bids (currentBid equals startingPrice)
      const currentBid = listing.auctionDetails?.currentBid || 0;
      const startingPrice = listing.auctionDetails?.startingPrice || 0;
      return currentBid === startingPrice;
    }

    return false;
  };

  const handleCancelListing = () => {
    setShowCancelConfirmDialog(true);
  };

  const handleConfirmCancelListing = async () => {
    if (!listing?.id) return;

    try {
      await cancelListingAsync(listing.id);
      // Close the confirmation dialog
      setShowCancelConfirmDialog(false);
    } catch (error) {}
  };

  // Helper function to check if current time is within listing time range
  const isCurrentTimeInListingRange = () => {
    if (!listing) return false;
    const now = new Date();
    const startTime = new Date(listing.startTime);
    const endTime = new Date(listing.endTime);
    return now >= startTime && now <= endTime;
  };

  // Handler for "Reservera" button
  const handleReserveListing = async () => {
    if (!listing) return;
    try {
      await updateListingAsync({
        id: listing.id,
        status: "reserved",
      });
    } catch (error) {}
  };

  // Handler for "Aktivera annons" button
  const handleActivateListing = async () => {
    if (!listing) return;
    try {
      await updateListingAsync({
        id: listing.id,
        status: "active",
      });
    } catch (error) {}
  };

  // Handler for opening statistics dialog
  const handleStatisticsClick = async () => {
    if (!listing) return;

    // Direct purchase: open without fetching bids
    if (listing.listingType === "direct-purchase") {
      setStatsData({
        totalViews: listing.bidding?.views || listing.views || 0,
        uniqueViewers:
          listing.bidding?.uniqueViews ?? listing.uniqueViews ?? 0,
        totalBids: 0,
        uniqueBidders: 0,
        currentPrice: `${(listing.directPurchaseDetails?.currentPrice || 0).toLocaleString("sv-SE")} :-`,
        startingPrice: `${(listing.directPurchaseDetails?.originalPrice || listing.directPurchaseDetails?.currentPrice || 0).toLocaleString("sv-SE")} :-`,
        bids: [],
        endDate: listing.endTime
          ? toInputDate(new Date(listing.endTime))
          : "",
      });
      setShowStats(true);
      return;
    }

    setIsLoadingStats(true);

    try {
      // Fetch bids first, then open dialog – avoids empty bid history on first open
      const { data: refetched } = await listingBidsQuery.refetch();
      const bids =
        (
          refetched as
            | {
                data?: Array<{
                  id: string;
                  amount: number;
                  bidFee: number;
                  bidderId: string;
                  createdAt: string;
                  bidderName?: string;
                  bidderEmail?: string;
                }>;
              }
            | undefined
        )?.data ?? [];

      // Get highest bid amount
      const highestBidAmount =
        bids.length > 0 ? Math.max(...bids.map((b) => b.amount || 0)) : 0;

      // Map bids to format expected by dialog, sorted by amount descending
      const sortedBids = [...bids].sort((a, b) => b.amount - a.amount);
      const bidHistory = sortedBids.map((bid, index) => {
        const isHighest = index === 0 && bid.amount === highestBidAmount;
        let bidderName = "";

        if (revealBidderIdentities && bid.bidderName) {
          bidderName = bid.bidderName;
        } else {
          // Without detaljerad statistik: hidden like ordinary customer/seller view
          bidderName = "•".repeat(8);
        }

        const locale = language === "en" ? "en-GB" : "sv-SE";
        return {
          id: bid.id,
          bidder: bidderName,
          amount: `${bid.amount.toLocaleString(locale)} :-`,
          timestamp: new Date(bid.createdAt).toLocaleString(locale, {
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
          }),
          isHighest,
          ...(revealBidderIdentities && bid.bidderEmail
            ? { email: bid.bidderEmail }
            : {}),
        };
      });

      // Calculate unique bidders
      const uniqueBidderIds = new Set(bids.map((b) => b.bidderId));
      const uniqueBidders = uniqueBidderIds.size;

      const currentPriceValue =
        listing.listingType === "auction"
          ? auctionDisplayPrice
          : listing.directPurchaseDetails?.currentPrice || 0;
      const startingPriceValue =
        listing.listingType === "auction"
          ? listing.auctionDetails?.startingPrice || 0
          : listing.directPurchaseDetails?.currentPrice || 0;

      setStatsData({
        totalViews: listing.bidding?.views || listing.views || 0,
        uniqueViewers:
          listing.bidding?.uniqueViews ?? listing.uniqueViews ?? 0,
        totalBids: bids.length,
        uniqueBidders,
        currentPrice: `${currentPriceValue.toLocaleString("sv-SE")} :-`,
        startingPrice: `${startingPriceValue.toLocaleString("sv-SE")} :-`,
        requestedPrice:
          listing.requestedPrice != null
            ? `${listing.requestedPrice.toLocaleString("sv-SE")} :-`
            : undefined,
        reservationPrice:
          listing.listingType === "auction" &&
          listing.auctionDetails?.reservationPrice != null &&
          listing.auctionDetails.reservationPrice > 0
            ? `${listing.auctionDetails.reservationPrice.toLocaleString("sv-SE")} :-`
            : undefined,
        endDate: listing.endTime
          ? toInputDate(new Date(listing.endTime))
          : listing.status === "completed"
            ? "Slutförd"
            : "Ongoing",
        bids: bidHistory,
      });
      setShowStats(true);
    } catch (error) {
      // Fallback stats – still open so user sees something
      setStatsData({
        totalViews: listing.bidding?.views || listing.views || 0,
        uniqueViewers:
          listing.bidding?.uniqueViews ?? listing.uniqueViews ?? 0,
        totalBids: listing.bidding?.totalBids || 0,
        uniqueBidders: 0,
        currentPrice: `${(listing.listingType === "auction"
          ? auctionDisplayPrice
          : listing.directPurchaseDetails?.currentPrice || 0
        ).toLocaleString("sv-SE")} :-`,
        startingPrice: `${(listing.listingType === "auction"
          ? listing.auctionDetails?.startingPrice || 0
          : listing.directPurchaseDetails?.currentPrice || 0
        ).toLocaleString("sv-SE")} :-`,
        requestedPrice:
          listing.requestedPrice != null
            ? `${listing.requestedPrice.toLocaleString("sv-SE")} :-`
            : undefined,
        reservationPrice:
          listing.listingType === "auction" &&
          listing.auctionDetails?.reservationPrice != null &&
          listing.auctionDetails.reservationPrice > 0
            ? `${listing.auctionDetails.reservationPrice.toLocaleString("sv-SE")} :-`
            : undefined,
        endDate: listing.endTime
          ? toInputDate(new Date(listing.endTime))
          : "",
        bids: [],
      });
      setShowStats(true);
    } finally {
      setIsLoadingStats(false);
    }
  };

  return (
    <>
      <div className="space-y-6">
        <div className="aspect-video rounded-lg overflow-hidden p-4">
          {listing.thumbnail ? (
            <img
              src={listing.thumbnail}
              alt={listing.vehicleDetails?.make || "Unknown"}
              className="w-full h-full object-cover rounded-lg"
            />
          ) : (
            <div className="w-full h-full bg-primary/40 flex items-center justify-center rounded-lg">
              <span className="text-gray-500">Ingen bild tillgänglig</span>
            </div>
          )}
        </div>

        {/* Vehicle Information */}
        <Card className="p-4">
          <div className="flex flex-col gap-4 mb-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className={getTypeColor(listing.listingType)}>
                {getTypeLabel(listing.listingType)}
              </Badge>
              <Badge
                variant="outline"
                className={getVehicleStatusColor(listing.status)}
              >
                {getVehicleStatusLabel(listing.status)}
              </Badge>
            </div>
            <div className="flex flex-col gap-2 w-full sm:w-auto sm:min-w-[9rem] sm:max-w-[11rem]">
              <Button
                onClick={handleStatisticsClick}
                variant="outline"
                size="sm"
                className="w-full"
                disabled={isLoadingStats}
              >
                <BarChart3 className="h-4 w-4 mr-1" />
                {isLoadingStats ? "Laddar…" : "Statistik"}
              </Button>
              <Button
                onClick={handleGoToListing}
                variant="outline"
                size="sm"
                className="w-full"
              >
                <ExternalLink className="h-4 w-4 mr-1" />
                {L.goToListing}
              </Button>

              {/* Edit for draft (scheduled), active, cancelled, without-campaign */}
              {(listing.status === "draft" ||
                listing.status === "active" ||
                listing.status === "cancelled" ||
                listing.status === "without-campaign") && (
                <div className="space-y-2 w-full">
                  <Button
                    onClick={handleStartEditing}
                    variant="outline"
                    size="sm"
                    className="w-full"
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    {L.editListing}
                  </Button>
                  {vehicleTest && (
                    <Button
                      onClick={() => setShowEditTestDialog(true)}
                      variant="outline"
                      size="sm"
                      className="w-full"
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      {L.editTest}
                    </Button>
                  )}
                  {canCancelListing() && (
                    <Button
                      onClick={handleCancelListing}
                      variant="outline"
                      size="sm"
                      className="w-full text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      {L.cancelListing ?? "Avbryt annons"}
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h2 className="font-bold text-xl mb-2">
                {listing.title || "Annons"}
              </h2>
              <h3 className="font-semibold text-lg text-muted-foreground">
                {listing.vehicleDetails?.make || "Unknown"}{" "}
                {listing.vehicleDetails?.model || "Unknown"}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <LicensePlate
                  lpn={listing.vehicleDetails?.regNumber || "Unknown"}
                />
                <span className="text-sm text-muted-foreground">
                  ({listing.vehicleDetails?.year || "Unknown"})
                </span>
              </div>
            </div>

            {listing.vehicleDetails && (
              <div className="pt-8">
                <VehicleInformation
                  vehicleDetails={listing.vehicleDetails}
                  vehicleTest={vehicleTest}
                  equipment={
                    vehicleTest?.vehicleDetails.equipment ||
                    listing.vehicleDetails.equipment
                  }
                  showAdditionalInfo={true}
                />
              </div>
            )}

            <div className="pt-4 border-t">
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-500" />
                <span className="font-medium">
                  {listing.listingType === "auction" &&
                  listing.status === "active"
                    ? (L.currentHighestBid ?? "Nuvarande högsta bud:")
                    : listing.listingType === "auction"
                      ? (L.finalPrice ?? "Slutpris:")
                      : (L.priceLabel ?? "Pris:")}
                </span>
                <span className="text-lg font-bold text-green-500">
                  {listing.listingType === "auction"
                    ? auctionDisplayPrice.toLocaleString()
                    : (
                        listing.directPurchaseDetails?.currentPrice || 0
                      ).toLocaleString()}{" "}
                  kr
                </span>
              </div>
            </div>

            {(listing.startTime || listing.endTime) && (
              <div className="pt-2 border-t space-y-1">
                {listing.startTime && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="font-medium">
                      {L.startTime?.replace(/\s*\*$/, "") || "Starttid"}:
                    </span>
                    <span>{formatDateTime(listing.startTime, language)}</span>
                  </div>
                )}
                {listing.endTime && (
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0 opacity-0" />
                    <span className="font-medium">
                      {L.endTime?.replace(/\s*\*$/, "") || "Sluttid"}:
                    </span>
                    <span>{formatDateTime(listing.endTime, language)}</span>
                  </div>
                )}
              </div>
            )}

            {listing.listingType === "auction" &&
              listing.auctionDetails?.autoRenewIfReserveNotReached && (
                <div className="pt-2 border-t">
                  <p className="text-sm text-muted-foreground">
                    {(L as Record<string, string>).autoRenewOverview ??
                      "Förnyas automatiskt om reservpris ej nått."}
                    {listing.auctionDetails?.autoRenewDays != null && (
                      <span className="ml-1 font-medium text-foreground">
                        {(
                          (L as Record<string, string>).autoRenewOverviewDays ??
                          "Förnyas med {days} dagar."
                        ).replace(
                          "{days}",
                          String(listing.auctionDetails.autoRenewDays),
                        )}
                      </span>
                    )}
                  </p>
                </div>
              )}

            {listing.campaignId && (
              <div className="pt-2 border-t">
                <span className="font-bold">{L.campaign ?? "Kampanj:"}</span>
                <span className="ml-2">
                  {campaign?.name || listing.campaignId}
                </span>
              </div>
            )}
          </div>
        </Card>

        {listing.description && (
          <Card className="p-6">
            <h3 className="font-semibold mb-2">
              {L.description ?? "Beskrivning"}
            </h3>
            <p className="text-muted-foreground">{listing.description}</p>
          </Card>
        )}
      </div>

      {/* Dialogs */}

      {/* Cancel Listing Confirmation Dialog */}
      <Dialog
        open={showCancelConfirmDialog}
        onOpenChange={setShowCancelConfirmDialog}
      >
        <DialogContent nested>
          <DialogHeader>
            <DialogTitle>Är du säker?</DialogTitle>
            <DialogDescription>
              Du är på väg att avbryta denna annons. När du avbryter annonsen
              kommer den att:
              <ul className="mt-2 ml-4 list-disc space-y-1">
                <li>Försvinna från sökresultat och kategorier</li>
                <li>
                  Inte längre vara tillgänglig för användare att se eller buda
                  på
                </li>
                <li>Markeras som avbruten i systemet</li>
                <li>Inte kunna återaktiveras utan att skapa en ny annons</li>
              </ul>
              <p className="mt-3 font-medium text-red-600">
                Denna åtgärd kan inte ångras.
              </p>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowCancelConfirmDialog(false)}
            >
              {L.cancel ?? "Avbryt"}
            </Button>
            <Button variant="destructive" onClick={handleConfirmCancelListing}>
              {L.yesCancelListing ?? "Ja, avbryt annonsen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {showStats && listing && (
        <MyAuctionsStatsDialog
          open={showStats}
          onOpenChange={setShowStats}
          stats={
            statsData || {
              totalViews: listing.bidding?.views || listing.views || 0,
              uniqueViewers:
                listing.bidding?.uniqueViews ?? listing.uniqueViews ?? 0,
              totalBids: listing.bidding?.totalBids || 0,
              uniqueBidders: 0, // Will be calculated from bids
              currentPrice: `${
                listing.listingType === "auction"
                  ? (
                      listing.auctionDetails?.currentBid ||
                      listing.auctionDetails?.startingPrice ||
                      0
                    ).toLocaleString("sv-SE")
                  : (
                      listing.directPurchaseDetails?.currentPrice || 0
                    ).toLocaleString("sv-SE")
              } :-`,
              startingPrice: `${
                listing.listingType === "auction"
                  ? (listing.auctionDetails?.startingPrice || 0).toLocaleString(
                      "sv-SE",
                    )
                  : (
                      listing.directPurchaseDetails?.currentPrice || 0
                    ).toLocaleString("sv-SE")
              } :-`,
              bids: [],
              endDate: listing.endTime
                ? toInputDate(new Date(listing.endTime))
                : "",
            }
          }
          vehicleName={`${listing?.vehicleDetails?.make || ""} ${
            listing?.vehicleDetails?.model || ""
          }`}
          regNumber={listing?.vehicleDetails?.regNumber}
          isLoading={isLoadingStats}
          isAdmin={revealBidderIdentities}
          hideBids={listing.listingType === "direct-purchase"}
          sellerName={resolveSellerDisplayName(listing.seller)}
          sellerEmail={listing.seller?.email?.trim() || undefined}
          sellerSubtitle={resolveSellerSubtitle(listing.seller)}
        />
      )}

      {showEditListing && (
        <CreateListingDialog
          open={showEditListing}
          onOpenChange={setShowEditListing}
          existingListing={listing as ListingLeadbox}
          isEditMode={true}
        />
      )}

      {showEditTestDialog && listing && vehicleTest && (
        <EditTestDialog
          test={vehicleTest}
          isOpen={showEditTestDialog}
          onOpenChange={setShowEditTestDialog}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          listingId={listing.id}
          listingType={listing.listingType}
        />
      )}
    </>
  );
};
